import css from "./ExplorePage.sass?inline"
import {createElement, PageContext, PageFactory} from "@opendaw/lib-jsx"
import {StudioService} from "@/service/StudioService"
import {Html} from "@opendaw/lib-dom"
import {getSharedTracks, onTracksChange, formatDuration} from "@/ui/pages/TrackStore"
import {addFlashlightEffect} from "@/ui/pages/FlashlightEffect"
import {openPlayer} from "@/ui/pages/MusicPlayerPanel"
import {downloadTrackAudio} from "@/ui/pages/AudioDownload"

const className = Html.adoptStyleSheet(css, "ExplorePage")

const GENRES = [
    {name: "Pop", gradient: "linear-gradient(135deg, hsl(310, 80%, 55%), hsl(340, 90%, 60%))"},
    {name: "Country", gradient: "linear-gradient(135deg, hsl(30, 80%, 50%), hsl(40, 90%, 55%))"},
    {name: "Rock", gradient: "linear-gradient(135deg, hsl(15, 80%, 50%), hsl(30, 85%, 55%))"},
    {name: "Electronic", gradient: "linear-gradient(135deg, hsl(180, 70%, 40%), hsl(200, 80%, 50%))"},
    {name: "Latin", gradient: "linear-gradient(135deg, hsl(80, 70%, 40%), hsl(140, 60%, 45%))"},
    {name: "Hip-Hop", gradient: "linear-gradient(135deg, hsl(270, 70%, 45%), hsl(300, 60%, 50%))"},
    {name: "R&B", gradient: "linear-gradient(135deg, hsl(240, 60%, 45%), hsl(270, 70%, 50%))"},
    {name: "Afrobeats", gradient: "linear-gradient(135deg, hsl(20, 80%, 45%), hsl(340, 70%, 50%))"},
]

export const ExplorePage: PageFactory<StudioService> = ({}: PageContext<StudioService>) => {
    // Genre cards
    const genreGrid: HTMLDivElement = <div className="genre-grid"/> as unknown as HTMLDivElement
    for (const genre of GENRES) {
        const card: HTMLDivElement = (
            <div className="genre-card" style={{background: genre.gradient}}>
                {genre.name}
            </div>
        ) as unknown as HTMLDivElement
        genreGrid.appendChild(card)
    }

    // Community tracks (shared tracks from store)
    const communitySection: HTMLDivElement = (
        <div className="section">
            <h2>Community Tracks</h2>
            <div className="community-list"/>
        </div>
    ) as unknown as HTMLDivElement
    const communityList = communitySection.querySelector(".community-list")!

    function renderCommunityTracks(): void {
        communityList.innerHTML = ""
        const shared = getSharedTracks()

        if (shared.length === 0) {
            const empty: HTMLDivElement = (
                <div className="community-empty">
                    <span>No shared tracks yet. Create a track with Share enabled to see it here.</span>
                </div>
            ) as unknown as HTMLDivElement
            communityList.appendChild(empty)
            return
        }

        for (const track of shared) {
            const timeAgo = getTimeAgo(track.createdAt)
            const dlWrap: HTMLDivElement = (
                <div className="community-dl-wrap">
                    <button className="community-dl-btn">{"\u2193"}</button>
                    <div className="community-dl-menu">
                        <button className="community-dl-opt">MP3</button>
                        <button className="community-dl-opt">WAV</button>
                    </div>
                </div>
            ) as unknown as HTMLDivElement
            const dlBtn = dlWrap.querySelector(".community-dl-btn") as HTMLButtonElement
            const dlMenu = dlWrap.querySelector(".community-dl-menu") as HTMLDivElement
            dlBtn.onclick = (event) => { event.stopPropagation(); dlMenu.classList.toggle("open") }
            document.addEventListener("click", () => dlMenu.classList.remove("open"))
            const [mp3Opt, wavOpt] = dlMenu.querySelectorAll(".community-dl-opt") as unknown as HTMLButtonElement[]
            mp3Opt.onclick = (event) => {
                event.stopPropagation()
                dlMenu.classList.remove("open")
                if (track.audioUrl) downloadTrackAudio(track.audioUrl, track.title, "mp3").catch(console.error)
            }
            wavOpt.onclick = (event) => {
                event.stopPropagation()
                dlMenu.classList.remove("open")
                if (track.audioUrl) downloadTrackAudio(track.audioUrl, track.title, "wav").catch(console.error)
            }
            const row: HTMLDivElement = (
                <div className="community-item">
                    <div className="community-art">{"\u266B"}</div>
                    <div className="community-info">
                        <div className="community-title">{track.title}</div>
                        <div className="community-desc">{track.description}</div>
                        <div className="community-meta">{track.model} &middot; {track.bpm} BPM &middot; {formatDuration(track.duration)} &middot; {timeAgo}</div>
                    </div>
                    {dlWrap}
                </div>
            ) as unknown as HTMLDivElement
            row.onclick = () => openPlayer(track)
            communityList.appendChild(row)
        }
    }

    renderCommunityTracks()
    onTracksChange(renderCommunityTracks)

    const root: HTMLDivElement = (
        <div className={className}>
            <div className="explore-content">
                <h1>Explore</h1>
                <div className="section">
                    <h2>Best Of <button className="see-all">See all {">"}</button></h2>
                    {genreGrid}
                </div>
                {communitySection}
            </div>
        </div>
    ) as unknown as HTMLDivElement
    addFlashlightEffect(root)
    return root
}

function getTimeAgo(ts: number): string {
    const diff = Date.now() - ts
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return "Just now"
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d ago`
    return new Date(ts).toLocaleDateString()
}
